import React, { useEffect, useState, useCallback } from 'react';
import { Rocket, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAdCampaigns, createAdCampaign, launchAdCampaign, deleteAdCampaign, getAutoContent } from '../../utils/api';
import { StatusBadge, PLATFORM_LABELS, formatDateTime } from './shared';

const inputCls =
  'w-full bg-black border border-divider rounded-lg px-3 py-2 text-white text-sm focus:border-lime focus:outline-none';

export const AdCampaigns = ({ clientId, refreshKey, onChanged }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [approvedAds, setApprovedAds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '', objective: 'leads', platform: 'meta_ads', daily_budget: 50,
    locations: '', age_min: 25, age_max: 65, content_item_ids: [],
  });

  const fetchAll = useCallback(async () => {
    try {
      const [cRes, aRes] = await Promise.all([
        getAdCampaigns({ client_id: clientId }),
        getAutoContent({ client_id: clientId, type: 'ad', status: 'approved' }),
      ]);
      setCampaigns(cRes.data.campaigns);
      setApprovedAds(aRes.data.items);
    } catch (err) {
      toast.error('Failed to load ad campaigns');
    }
  }, [clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const toggleAd = (id) => {
    setForm((f) => ({
      ...f,
      content_item_ids: f.content_item_ids.includes(id)
        ? f.content_item_ids.filter((x) => x !== id)
        : [...f.content_item_ids, id],
    }));
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Campaign name is required');
    if (form.content_item_ids.length === 0) return toast.error('Link at least one approved ad');
    try {
      await createAdCampaign({
        client_id: clientId,
        name: form.name.trim(),
        objective: form.objective,
        platform: form.platform,
        daily_budget: Number(form.daily_budget),
        targeting: {
          locations: form.locations.split(',').map((s) => s.trim()).filter(Boolean),
          age_min: Number(form.age_min),
          age_max: Number(form.age_max),
        },
        content_item_ids: form.content_item_ids,
      });
      toast.success('Ad campaign created (draft)');
      setShowForm(false);
      setForm({ name: '', objective: 'leads', platform: 'meta_ads', daily_budget: 50, locations: '', age_min: 25, age_max: 65, content_item_ids: [] });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const handleLaunch = async (id) => {
    try {
      const res = await launchAdCampaign(id);
      toast.success(res.data.launch_result?.sandbox ? 'Launched in sandbox mode 🚀' : 'Campaign launched 🚀');
      fetchAll();
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Launch failed');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdCampaign(id);
      toast.success('Campaign deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="ad-campaigns">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-bold text-lg">Ad Campaigns</h3>
          <p className="text-gray text-sm">Bundle approved ads into a campaign and launch to Meta or Google. Sandbox mode until API keys are added.</p>
        </div>
        <button
          data-testid="new-campaign-btn"
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 rounded-lg bg-orange text-white font-bold text-sm flex items-center gap-2 hover:bg-orange/90"
        >
          <Plus size={16} /> New Campaign
        </button>
      </div>

      {showForm && (
        <div className="bg-card-bg border border-orange/40 rounded-xl p-5 mb-6 space-y-4" data-testid="campaign-form">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="campaign-name-input" className={inputCls} placeholder="Campaign name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select data-testid="campaign-platform-select" className={inputCls} value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}>
              <option value="meta_ads">Meta Ads</option>
              <option value="google_ads">Google Ads</option>
            </select>
            <select data-testid="campaign-objective-select" className={inputCls} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}>
              {['leads', 'traffic', 'awareness', 'engagement', 'sales'].map((o) => (
                <option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <span className="text-gray text-sm whitespace-nowrap">Daily budget $</span>
              <input data-testid="campaign-budget-input" type="number" className={inputCls} value={form.daily_budget} onChange={(e) => setForm({ ...form, daily_budget: e.target.value })} />
            </div>
            <input data-testid="campaign-locations-input" className={inputCls} placeholder="Target locations (comma separated)" value={form.locations} onChange={(e) => setForm({ ...form, locations: e.target.value })} />
            <div className="flex items-center gap-2">
              <span className="text-gray text-sm">Age</span>
              <input type="number" className={inputCls} value={form.age_min} onChange={(e) => setForm({ ...form, age_min: e.target.value })} />
              <span className="text-gray text-sm">to</span>
              <input type="number" className={inputCls} value={form.age_max} onChange={(e) => setForm({ ...form, age_max: e.target.value })} />
            </div>
          </div>

          <div>
            <p className="text-xs text-gray uppercase font-semibold mb-2">Link Approved Ads ({approvedAds.length} available)</p>
            {approvedAds.length === 0 ? (
              <p className="text-gray text-sm">No approved ads yet. Generate ad content and approve it first.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {approvedAds.map((ad) => (
                  <label key={ad.id} className="flex items-start gap-3 p-3 bg-black rounded-lg border border-divider cursor-pointer hover:border-gray">
                    <input
                      type="checkbox"
                      data-testid={`link-ad-${ad.id}`}
                      checked={form.content_item_ids.includes(ad.id)}
                      onChange={() => toggleAd(ad.id)}
                      className="mt-1 accent-lime"
                    />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium">{ad.title}</p>
                      <p className="text-gray text-xs truncate">{ad.body}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-light text-sm hover:text-white">Cancel</button>
            <button data-testid="campaign-create-btn" onClick={handleCreate} className="px-5 py-2 rounded-lg bg-orange text-white font-bold text-sm hover:bg-orange/90">
              Create Campaign
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {campaigns.length === 0 && !showForm && (
          <div className="text-center py-12 border border-dashed border-divider rounded-xl" data-testid="campaigns-empty">
            <p className="text-gray-light">No ad campaigns yet.</p>
          </div>
        )}
        {campaigns.map((c) => (
          <div key={c.id} data-testid={`campaign-${c.id}`} className="bg-card-bg border border-divider rounded-xl p-5 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-white font-bold text-lg">{c.name}</h4>
                <StatusBadge status={c.status} />
                {c.launch_result?.sandbox && <span className="text-xs text-orange">sandbox</span>}
              </div>
              <p className="text-gray text-sm">
                {PLATFORM_LABELS[c.platform]} · {c.objective} · ${c.daily_budget}/day · {c.content_item_ids.length} ad(s)
                {c.launched_at && ` · Launched ${formatDateTime(c.launched_at)}`}
              </p>
            </div>
            <div className="flex gap-2">
              {c.status !== 'launched' && (
                <>
                  <button
                    data-testid={`launch-btn-${c.id}`}
                    onClick={() => handleLaunch(c.id)}
                    className="px-4 py-2 rounded-lg bg-lime text-black text-sm font-bold flex items-center gap-2 hover:bg-lime/90"
                  >
                    <Rocket size={14} /> Launch
                  </button>
                  <button
                    data-testid={`delete-campaign-btn-${c.id}`}
                    onClick={() => handleDelete(c.id)}
                    className="p-2 rounded-lg border border-orange/40 text-orange hover:bg-orange/10"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
